/**
 * Page history, pure: how a version is named and a plain-text line diff of it
 * against the page as it is now. The versions themselves come from
 * GET /api/pages/{id}/history (the server's changelog; nothing is stored).
 */

export interface PageVersion {
  seq: number
  at: string
  who: 'owner' | 'prime' | 'member' | 'other'
  name: string
  action: string
  chars: number
  preview: string
  current?: boolean
}

export interface PageVersionFull {
  seq: number
  at: string
  who: PageVersion['who']
  name: string
  text: string
  blocks: unknown[]
}

/** "You", "Prime", a member's name; a device the owner no longer has is "Another device". */
export const writerLabel = (v: Pick<PageVersion, 'who' | 'name'>): string => {
  if (v.who === 'owner') return 'You'
  if (v.who === 'prime') return 'Prime'
  if (v.who === 'member') return v.name || 'A member'
  return 'Another device'
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** When, as people say it: the time today, "yesterday", a weekday within the week, else the date. */
export const whenLabel = (iso: string, now = new Date()): string => {
  const d = new Date(iso)
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000)
  if (days <= 0) return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (days === 1) return 'yesterday'
  if (days < 7) return d.toLocaleDateString('en-GB', { weekday: 'long' })
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** "Prime, 14:02", "You, yesterday". */
export const versionLabel = (v: Pick<PageVersion, 'who' | 'name' | 'at'>, now = new Date()): string =>
  `${writerLabel(v)}, ${whenLabel(v.at, now)}`

export type DiffLine = { op: 'same' | 'add' | 'del'; text: string }

/** Longest-common-subsequence cells past which the diff stops being worth the wait. */
const MAX_CELLS = 4_000_000

/**
 * Line diff from `from` to `to`: `del` is only in `from`, `add` only in `to`.
 * Past a size where the table would be slow it falls back to all of `from`
 * removed and all of `to` added, which is still true, only coarser.
 */
export const lineDiff = (from: string, to: string): DiffLine[] => {
  const a = from === '' ? [] : from.split('\n')
  const b = to === '' ? [] : to.split('\n')
  let head = 0
  while (head < a.length && head < b.length && a[head] === b[head]) head++
  let tail = 0
  while (
    tail < a.length - head &&
    tail < b.length - head &&
    a[a.length - 1 - tail] === b[b.length - 1 - tail]
  )
    tail++
  const am = a.slice(head, a.length - tail)
  const bm = b.slice(head, b.length - tail)
  const out: DiffLine[] = a.slice(0, head).map((text) => ({ op: 'same', text }))
  if ((am.length + 1) * (bm.length + 1) > MAX_CELLS) {
    for (const text of am) out.push({ op: 'del', text })
    for (const text of bm) out.push({ op: 'add', text })
  } else {
    const w = bm.length + 1
    const t = new Uint32Array((am.length + 1) * w)
    for (let i = am.length - 1; i >= 0; i--)
      for (let j = bm.length - 1; j >= 0; j--)
        t[i * w + j] =
          am[i] === bm[j] ? t[(i + 1) * w + j + 1]! + 1 : Math.max(t[(i + 1) * w + j]!, t[i * w + j + 1]!)
    let i = 0
    let j = 0
    while (i < am.length && j < bm.length) {
      if (am[i] === bm[j]) {
        out.push({ op: 'same', text: am[i]! })
        i++
        j++
      } else if (t[(i + 1) * w + j]! >= t[i * w + j + 1]!) out.push({ op: 'del', text: am[i++]! })
      else out.push({ op: 'add', text: bm[j++]! })
    }
    while (i < am.length) out.push({ op: 'del', text: am[i++]! })
    while (j < bm.length) out.push({ op: 'add', text: bm[j++]! })
  }
  for (const text of a.slice(a.length - tail)) out.push({ op: 'same', text })
  return out
}

export type DiffRow = DiffLine | { op: 'gap'; text: string; hidden: number }

/** Unchanged runs longer than 2×`context` fold to one "N unchanged lines" row. */
export const foldDiff = (lines: DiffLine[], context = 2): DiffRow[] => {
  const out: DiffRow[] = []
  let i = 0
  while (i < lines.length) {
    if (lines[i]!.op !== 'same') {
      out.push(lines[i++]!)
      continue
    }
    let j = i
    while (j < lines.length && lines[j]!.op === 'same') j++
    const run = lines.slice(i, j)
    const before = i === 0 ? 0 : context
    const after = j === lines.length ? 0 : context
    if (run.length > before + after + 1) {
      out.push(...run.slice(0, before))
      out.push({ op: 'gap', text: '', hidden: run.length - before - after })
      out.push(...run.slice(run.length - after))
    } else out.push(...run)
    i = j
  }
  return out
}

/** Whether the diff changes anything. */
export const isSame = (lines: DiffLine[]): boolean => lines.every((l) => l.op === 'same')
